import { Resource, type ResourceRepository, ResourceTypes } from '@telespot/domain';
import { ParseBaseRepository } from '../../parse-base.repository';
import { ParseResourceMapper, ResourceTopology } from './parse-resource.mapper';
import { ObjectTopology, SoftDeletableTopology } from '../../parse.topology';

export class ParseResourceRepository extends ParseBaseRepository implements ResourceRepository {
  private static readonly COUNT_RESOURCES_PAGINATION_LIMIT = 25;

  private readonly mapper = new ParseResourceMapper(this.parse);

  public async getById(id: string): Promise<Resource> {
    const resource = await new this.parse.Query(ResourceTopology.TABLE)
      .include(ResourceTopology.RESOURCES)
      .get(id, this.options);

    return this.mapper.toDomain(resource);
  }

  public async delete(id: string): Promise<void> {
    try {
      const parseResource = this.subclasses.getSubclass(ResourceTopology.TABLE).createWithoutData(id);
      await parseResource.save(
        {
          [SoftDeletableTopology.DELETED_AT]: new Date(),
        },
        this.options
      );
    } catch (error) {
      // Parse error codes: https://docs.parseplatform.org/js/guide/
      switch (error.code) {
        case 101: // ObjectNotFound
          return; // Object not found, that's ok
        default:
          throw error;
      }
    }
  }

  public async getAll(searchTerm?: string, limit?: number, lastItemName?: string, type?: ResourceTypes): Promise<Resource[]> {
    const query = this.buildResourceQuery(searchTerm, type);

    query.ascending(ResourceTopology.NAME);
    query.include(ResourceTopology.RESOURCES);

    if (limit) {
      query.limit(limit);
    }

    if (lastItemName) {
      query.greaterThan(ResourceTopology.NAME, lastItemName);
    }

    const parseResources = await query.find(this.options);
    return parseResources.map((pr) => this.mapper.toDomain(pr));
  }

  public async count(searchTerm?: string, type?: ResourceTypes): Promise<number> {
    let total = 0;
    let lastId: string | undefined;
    let continueQuerying = true;

    while (continueQuerying) {
      const query = this.buildResourceQuery(searchTerm, type);
      query.ascending(ObjectTopology.ID);
      query.select(); // Only fetch objectIds to minimize data transfer
      query.limit(ParseResourceRepository.COUNT_RESOURCES_PAGINATION_LIMIT);

      if (lastId) {
        query.greaterThan(ObjectTopology.ID, lastId);
      }

      const results = await query.find(this.options);
      total += results.length;

      continueQuerying = results.length === ParseResourceRepository.COUNT_RESOURCES_PAGINATION_LIMIT;
      if (continueQuerying && results.length > 0) {
        lastId = results.at(-1).id;
      }
    }

    return total;
  }

  public async save(resource: Resource): Promise<string> {
    const parseResource = this.mapper.fromDomain(resource);
    const saved = await parseResource.save(null, this.options);
    return saved.id;
  }

  private buildResourceQuery(searchTerm?: string, type?: ResourceTypes): Parse.Query {
    const query = new this.parse.Query(ResourceTopology.TABLE);

    if (searchTerm && searchTerm.trim() !== '') {
      query.matches(ResourceTopology.NAME, new RegExp(this.escapeRegExp(searchTerm), 'i'));
    }

    if (type) {
      query.equalTo(ResourceTopology.TYPE, type);
    }

    return query;
  }
}
